import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  commentBodyPatchSchema,
  commentStreamCommentSchema,
  commentStreamQuerySchema,
  commentStreamSchema,
  createCommentBodySchema,
  GROUP_MENTION_CONFIRM_THRESHOLD,
  parseGroupMentions,
  parseMentions,
  type BoardMember,
  type BoardMemberWithHistory,
  type CommentBodyPatch,
  type BoardContents,
  type CommentStream,
  type CommentStreamComment,
  type CommentStreamQuery,
  type CreateCommentBody,
  type WorkspaceMember,
} from '@molt/shared';

import { del, emptyResponseSchema, get, patch, post } from './api';
import { applyCardLastActivityToBoardContents } from './card-aging';
import { setBoardContentsQueries } from './cross-board-move';
import { queryKeys } from './query-keys';

/**
 * Locally declared so this module stays decoupled from the interface-language
 * context, matching `BoardFilterTranslate` in `./board-filter`. Unlike that one
 * it carries the third parameter, because two of the mention descriptions are
 * plural entries and `interfaceMessage` picks the form from `count`.
 */
export type CommentTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface GroupMentionOption {
  readonly id: 'group:board' | 'group:card';
  readonly username: 'board' | 'card';
  readonly label: string;
  readonly description: string;
  readonly notifyCount: number;
  readonly requiresConfirmation: boolean;
}

export interface MentionCandidate {
  readonly id: string;
  readonly username: string;
  readonly label: string;
  readonly description: string;
  readonly rank: 'card' | 'board' | 'workspace';
}

export type CommentMentionSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'mention'; readonly text: string; readonly member: BoardMember };

export function getCommentStream(
  cardId: string,
  query: Partial<CommentStreamQuery> = {},
): Promise<CommentStream> {
  const params = commentStreamQuerySchema.partial().parse(query);
  const search = new URLSearchParams();
  if (params.cursor !== undefined) search.set('cursor', params.cursor);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`;
  return get(
    `/cards/${encodeURIComponent(cardId)}/comments${suffix}`,
    commentStreamSchema,
  );
}

/**
 * ⚠ **Parsed with `commentStreamCommentSchema`, not with the base comment
 * view, and the difference is not cosmetic.** `z.object` **strips**: the route
 * declares `response: { 201: commentStreamCommentSchema }` and sends
 * `authorPresence` and `via` on every reply, so parsing the read-back with the
 * base view silently discarded both — and `onSuccess` then wrote that
 * amputated row straight into `queryKeys.cards.comments`, where a comment that
 * arrived by reload carried the two facts and one the reader had just posted
 * did not. Nothing was red anywhere, because both fields are `.optional()` on
 * the stream shape.
 *
 * ⚠ **They stay `.optional()` in `@molt/shared`, and that is measured rather
 * than lazy.** Making them plainly required costs 25 `tsc` errors across 9
 * files, 11 of them in 4 files this change does not own — see the fan-out 9
 * audit §4.2. The strip is what this parse fixes; the compile-time net on top
 * is a separate residual.
 */
export function createComment(
  cardId: string,
  body: CreateCommentBody,
): Promise<CommentStreamComment> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/comments`,
    commentStreamCommentSchema,
    createCommentBodySchema.parse(body),
  );
}

/** Parsed with the stream shape for the reason `createComment` states. */
export function updateCommentBody(
  commentId: string,
  body: CommentBodyPatch,
): Promise<CommentStreamComment> {
  return patch(
    `/comments/${encodeURIComponent(commentId)}/body`,
    commentStreamCommentSchema,
    commentBodyPatchSchema.parse(body),
  );
}

export function deleteComment(commentId: string): Promise<void> {
  return del(`/comments/${encodeURIComponent(commentId)}`, emptyResponseSchema).then(
    () => undefined,
  );
}

export function commentStreamQueryOptions(cardId: string, limit?: number) {
  return queryOptions({
    queryKey: queryKeys.cards.comments(cardId),
    queryFn: () => getCommentStream(cardId, limit === undefined ? {} : { limit }),
  });
}

export function groupMentionOptions(input: {
  readonly t: CommentTranslate;
  readonly boardMemberCount: number;
  readonly cardMemberCount: number;
  readonly currentUserId?: string | undefined;
  readonly currentUserOnCard: boolean;
}): readonly GroupMentionOption[] {
  const boardNotifyCount =
    input.currentUserId === undefined
      ? input.boardMemberCount
      : Math.max(0, input.boardMemberCount - 1);
  const cardNotifyCount =
    input.currentUserId !== undefined && input.currentUserOnCard
      ? Math.max(0, input.cardMemberCount - 1)
      : input.cardMemberCount;

  return [
    {
      id: 'group:board',
      username: 'board',
      label: '@board',
      description: input.t(
        'cardBack.comments.mention.boardNotify',
        { count: boardNotifyCount },
        boardNotifyCount,
      ),
      notifyCount: boardNotifyCount,
      requiresConfirmation: boardNotifyCount >= GROUP_MENTION_CONFIRM_THRESHOLD,
    },
    {
      id: 'group:card',
      username: 'card',
      label: '@card',
      description:
        cardNotifyCount === 0
          ? input.t('cardBack.comments.mention.cardNotifyNone')
          : input.t(
              'cardBack.comments.mention.cardNotify',
              { count: cardNotifyCount },
              cardNotifyCount,
            ),
      notifyCount: cardNotifyCount,
      requiresConfirmation: false,
    },
  ];
}

export function groupMentionConfirmation(
  body: string,
  options: readonly GroupMentionOption[],
): GroupMentionOption | null {
  const groups = new Set(parseGroupMentions(body));
  return (
    options.find(
      (option) => option.requiresConfirmation && groups.has(option.username),
    ) ?? null
  );
}

export function mentionCandidates(input: {
  readonly t: CommentTranslate;
  readonly boardMembers: readonly BoardMember[];
  readonly cardMembers: readonly { readonly id: string }[];
  readonly workspaceMembers?: readonly WorkspaceMember[] | undefined;
}): readonly MentionCandidate[] {
  const cardMemberIds = new Set(input.cardMembers.map((member) => member.id));
  const boardMemberIds = new Set(input.boardMembers.map((member) => member.id));
  const boardMemberUsernames = new Set(
    input.boardMembers.map((member) => member.username.toLowerCase()),
  );
  const boardCandidates = input.boardMembers.map((member) => {
    const rank = cardMemberIds.has(member.id) ? 'card' : 'board';
    return {
      id: member.id,
      username: member.username,
      label: mentionDisplayName(input.t, member),
      description:
        rank === 'card'
          ? input.t('cardBack.comments.mention.cardMember')
          : input.t('cardBack.comments.mention.boardMember'),
      rank,
    } satisfies MentionCandidate;
  });
  const workspaceCandidates =
    input.workspaceMembers
      ?.filter(
        (member) =>
          !boardMemberIds.has(member.id) &&
          !boardMemberUsernames.has(member.username.toLowerCase()),
      )
      .map(
        (member) =>
          ({
            id: member.id,
            username: member.username,
            label: mentionDisplayName(input.t, member),
            description: input.t('cardBack.comments.mention.workspaceMember'),
            rank: 'workspace',
          }) satisfies MentionCandidate,
      ) ?? [];

  return [...boardCandidates, ...workspaceCandidates].sort((left, right) => {
    const rankDelta = mentionRankWeight(left.rank) - mentionRankWeight(right.rank);
    if (rankDelta !== 0) return rankDelta;
    return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
  });
}

export function commentMentionSegments(
  text: string,
  members: readonly BoardMemberWithHistory[],
): readonly CommentMentionSegment[] {
  const mentionedHandles = new Set(parseMentions(text));
  if (mentionedHandles.size === 0 || members.length === 0) {
    return text === '' ? [] : [{ kind: 'text', text }];
  }

  const membersByUsername = new Map(
    members.map((member) => [member.username.toLowerCase(), member]),
  );
  /**
   * BE-44.6 (`FR-NOTIF-033` AC3): the handles as they were. The board member
   * list carries each member's relinquished handles (`formerUsernames`,
   * populated from `user_username_history`), so an older comment keeps
   * linking after a rename. Current holders win: history fills only handles
   * no member holds now, and first-writer-wins across members, so the map
   * cannot flip an `@` between two people depending on list order.
   */
  const formerByUsername = new Map<string, BoardMember>();
  for (const member of members) {
    for (const former of member.formerUsernames ?? []) {
      const key = former.toLowerCase();
      if (!membersByUsername.has(key) && !formerByUsername.has(key)) {
        formerByUsername.set(key, member);
      }
    }
  }
  const segments: CommentMentionSegment[] = [];
  const mentionPattern = /(^|[^A-Za-z0-9._/-])@([A-Za-z0-9][A-Za-z0-9._-]*)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionPattern.exec(text)) !== null) {
    const prefix = match[1] ?? '';
    const handle = match[2] ?? '';
    const username = trimMentionHandle(handle).toLowerCase();
    const member =
      username === '' || !mentionedHandles.has(username)
        ? undefined
        : (membersByUsername.get(username) ?? formerByUsername.get(username));

    if (member === undefined) continue;

    const start = match.index + prefix.length;
    const end = start + 1 + username.length;
    if (start > cursor) segments.push({ kind: 'text', text: text.slice(cursor, start) });
    segments.push({ kind: 'mention', text: text.slice(start, end), member });
    cursor = end;
  }

  if (cursor < text.length) segments.push({ kind: 'text', text: text.slice(cursor) });
  return segments.length === 0 ? [{ kind: 'text', text }] : segments;
}

function mentionRankWeight(rank: MentionCandidate['rank']): number {
  if (rank === 'card') return 0;
  if (rank === 'board') return 1;
  return 2;
}

function mentionDisplayName(
  t: CommentTranslate,
  member: Pick<BoardMember, 'displayName' | 'username'>,
): string {
  const displayName = member.displayName?.trim();
  if (displayName !== undefined && displayName !== '') return displayName;
  const username = member.username.trim();
  return username === '' ? t('primitives.avatar.anonymousMember') : `@${username}`;
}

function trimMentionHandle(handle: string): string {
  return handle.replace(/[._-]+$/u, '');
}

export interface CreateCommentVariables {
  readonly boardId: string;
  readonly cardId: string;
  readonly body: string;
}

export interface UpdateCommentVariables {
  readonly boardId?: string | undefined;
  readonly cardId: string;
  readonly commentId: string;
  readonly body: string;
}

export interface DeleteCommentVariables {
  readonly boardId: string;
  readonly cardId: string;
  readonly commentId: string;
}

export function prependCommentToStream(
  stream: CommentStream | undefined,
  comment: CommentStreamComment,
): CommentStream | undefined {
  return stream === undefined
    ? stream
    : { ...stream, comments: [comment, ...stream.comments] };
}

export function replaceCommentInStream(
  stream: CommentStream | undefined,
  comment: CommentStreamComment,
): CommentStream | undefined {
  return stream === undefined
    ? stream
    : {
        ...stream,
        comments: stream.comments.map((existing) =>
          existing.id === comment.id ? comment : existing,
        ),
      };
}

export function removeCommentFromStream(
  stream: CommentStream | undefined,
  commentId: string,
): CommentStream | undefined {
  return stream === undefined
    ? stream
    : {
        ...stream,
        comments: stream.comments.filter((comment) => comment.id !== commentId),
      };
}

export function quoteCommentBody(input: {
  readonly body: string;
  readonly authorUsername?: string | undefined;
  readonly selection?: string | undefined;
}): string {
  const selected = input.selection?.trim();
  const quoted = (selected && selected.length > 0 ? selected : input.body).trim();
  const quote = quoted
    .split(/\r?\n/)
    .map((line) => `> ${line}`)
    .join('\n');
  const mention =
    input.authorUsername === undefined || input.authorUsername.trim() === ''
      ? ''
      : `@${input.authorUsername.trim()} `;
  return `${quote}\n\n${mention}`;
}

export function updateBoardContentsCommentCount(
  contents: BoardContents | undefined,
  cardId: string,
  delta: 1 | -1,
): BoardContents | undefined {
  if (contents === undefined) return contents;
  return {
    ...contents,
    lists: contents.lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              commentCount: Math.max(0, card.commentCount + delta),
            }
          : card,
      ),
    })),
  };
}

export function useCreateComment(): UseMutationResult<
  CommentStreamComment,
  Error,
  CreateCommentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, body }) => createComment(cardId, { body }),
    onSuccess: (comment, variables) => {
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) => prependCommentToStream(stream, comment),
      );
      setBoardContentsQueries(queryClient, variables.boardId, (contents) =>
        applyCardLastActivityToBoardContents(
          updateBoardContentsCommentCount(contents, variables.cardId, 1),
          variables.cardId,
          comment.createdAt,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
    },
  });
}

export function useUpdateComment(): UseMutationResult<
  CommentStreamComment,
  Error,
  UpdateCommentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, body }) => updateCommentBody(commentId, { body }),
    onSuccess: (comment, variables) => {
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) => replaceCommentInStream(stream, comment),
      );
      if (variables.boardId !== undefined) {
        setBoardContentsQueries(queryClient, variables.boardId, (contents) =>
          applyCardLastActivityToBoardContents(
            contents,
            variables.cardId,
            comment.editedAt ?? comment.createdAt,
          ),
        );
      }
    },
  });
}

export function useDeleteComment(): UseMutationResult<
  void,
  Error,
  DeleteCommentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId }) => deleteComment(commentId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData<CommentStream>(
        queryKeys.cards.comments(variables.cardId),
        (stream) => removeCommentFromStream(stream, variables.commentId),
      );
      setBoardContentsQueries(queryClient, variables.boardId, (contents) =>
        updateBoardContentsCommentCount(contents, variables.cardId, -1),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
    },
  });
}

export function commentPermissionMessage(
  t: CommentTranslate,
  stream: Pick<CommentStream, 'mayComment' | 'commentingPermission'>,
  signedIn: boolean,
  boardClosed = false,
): { title: string; body: string } | null {
  if (boardClosed) {
    return {
      title: t('cardBack.comments.permission.closed.title'),
      body: t('cardBack.comments.permission.closed.body'),
    };
  }
  if (stream.mayComment) return null;
  if (!signedIn && stream.commentingPermission === 'any_user') {
    return {
      title: t('cardBack.comments.permission.signIn.title'),
      body: t('cardBack.comments.permission.signIn.body'),
    };
  }
  switch (stream.commentingPermission) {
    case 'disabled':
      return {
        title: t('cardBack.comments.permission.disabled.title'),
        body: t('cardBack.comments.permission.disabled.body'),
      };
    case 'members':
      return {
        title: t('cardBack.comments.permission.members.title'),
        body: t('cardBack.comments.permission.readOnlyBody'),
      };
    case 'members_and_observers':
      return {
        title: t('cardBack.comments.permission.membersAndObservers.title'),
        body: t('cardBack.comments.permission.readOnlyBody'),
      };
    case 'workspace_members':
      return {
        title: t('cardBack.comments.permission.workspaceMembers.title'),
        body: t('cardBack.comments.permission.readOnlyBody'),
      };
    case 'any_user':
      return {
        title: t('cardBack.comments.permission.anyUser.title'),
        body: t('cardBack.comments.permission.anyUser.body'),
      };
  }
}
